import { useEffect } from "react";
import { X, TrendingUp } from "lucide-react";
import { formatMoney, monthLabel } from "@/lib/format";
import Sparkline from "@/components/Sparkline";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Slide-in project detail panel. Shows monthly breakdown for a single project
 * plus a mini line-chart. Opened from the Per-Project row click.
 */
export default function ProjectDetailDrawer({ project, months, milestones = [], open, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !project) return null;

  const monthly = project.monthly || [];
  const total = project.projected_total || 0;
  const peakIdx = monthly.reduce((iMax, v, i) => (v > monthly[iMax] ? i : iMax), 0);
  const avg = monthly.length ? total / monthly.length : 0;
  const chartData = months.map((m, i) => ({
    month: m,
    label: monthLabel(m),
    projected: monthly[i] || 0,
    isPeak: i === peakIdx,
    isMilestone: (project.milestone_months || []).includes(m),
  }));

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="project-drawer">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative ml-auto w-full max-w-[640px] h-full bg-paper overflow-y-auto"
        style={{ animation: "cf-drawer-in 0.28s cubic-bezier(.22,.61,.36,1) both" }}
      >
        <style>{`@keyframes cf-drawer-in { from { transform: translateX(30px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-paper/95 backdrop-blur border-b border-hairline/60">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-light font-semibold">
              {project.project_type} project
            </div>
            <div className="font-display text-2xl text-ink mt-0.5" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              {project.project_name}
            </div>
          </div>
          <button
            data-testid="project-drawer-close"
            onClick={onClose}
            className="cf-touch p-2 rounded-lg text-muted-dark hover:text-ink hover:bg-surface"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Summary tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile
              label="Horizon Total"
              value={formatMoney(total)}
              tone="gold"
              testId="pd-total"
            />
            <StatTile
              label="Peak Month"
              value={monthly.length ? formatMoney(monthly[peakIdx]) : "–"}
              sub={monthly.length ? monthLabel(months[peakIdx]) : "—"}
              testId="pd-peak"
            />
            <StatTile
              label="Avg / Month"
              value={formatMoney(avg)}
              testId="pd-avg"
            />
          </div>

          {/* Mini chart */}
          <div className="cf-card p-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold mb-3">
              Monthly Projection
            </div>
            <div style={{ height: 220, width: "100%" }}>
              <ResponsiveContainer width="100%" height={220} minWidth={200}>
                <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pd-area" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FBC02D" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#FBC02D" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="pd-line" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#E8531F" />
                      <stop offset="100%" stopColor="#FBC02D" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E4E1D6" strokeDasharray="0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#8A8776"
                    fontSize={10}
                    tickLine={false}
                    axisLine={{ stroke: "#E4E1D6" }}
                    interval="preserveStartEnd"
                    minTickGap={28}
                  />
                  <YAxis
                    stroke="#8A8776"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v >= 100 ? `₹${(v / 100).toFixed(0)}Cr` : `₹${v.toFixed(0)}L`)}
                    width={52}
                  />
                  <Tooltip
                    contentStyle={{ background: "#FFF", border: "1px solid #E4E1D6", borderRadius: 8, boxShadow: "0 8px 20px rgba(18,49,89,0.08)", fontSize: 12 }}
                    formatter={(v) => [formatMoney(v), "Projected"]}
                  />
                  <Area type="monotone" dataKey="projected" stroke="url(#pd-line)" strokeWidth={2.5} fill="url(#pd-area)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly table */}
          <div className="cf-card overflow-hidden">
            <div className="px-5 py-3 border-b border-hairline/60">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">
                Month-by-Month
              </div>
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/40">
                    <th className="text-left font-semibold px-5 py-2.5">Month</th>
                    <th className="text-right font-semibold px-5 py-2.5">Projected</th>
                    <th className="text-right font-semibold px-5 py-2.5">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((r) => (
                    <tr
                      key={r.month}
                      className={`border-b border-hairline/30 last:border-0 ${r.isPeak ? "cf-row-warn" : ""}`}
                    >
                      <td className="px-5 py-2 text-sm num text-ink">{r.label}</td>
                      <td className="px-5 py-2 text-right num text-sm text-ink font-medium">
                        {formatMoney(r.projected)}
                      </td>
                      <td className="px-5 py-2 text-right text-[11px] uppercase tracking-widest text-muted-light">
                        {r.isPeak && (
                          <span className="cf-pill cf-pill-warn inline-flex items-center gap-1">
                            <TrendingUp size={10} /> Peak
                          </span>
                        )}
                        {!r.isPeak && r.isMilestone && (
                          <span className="cf-pill cf-pill-ink">Milestone</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Milestones list — actual data for this project */}
          {milestones.length > 0 && (
            <div className="cf-card overflow-hidden">
              <div className="px-5 py-3 border-b border-hairline/60 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">
                  Milestones · {milestones.length}
                </div>
                <div className="text-[10px] text-muted-light num">
                  Σ {(milestones.reduce((s, m) => s + (m.milestone_pct || 0), 0) * 100).toFixed(1)}%
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-surface/90 backdrop-blur">
                    <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/40">
                      <th className="text-left font-semibold px-4 py-2">Milestone</th>
                      <th className="text-left font-semibold px-4 py-2">Demand</th>
                      <th className="text-right font-semibold px-4 py-2">%</th>
                      <th className="text-right font-semibold px-4 py-2">Lag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...milestones].sort((a, b) => (a.demand_month || "").localeCompare(b.demand_month || "")).map((m) => (
                      <tr key={m.id} className="border-b border-hairline/30 last:border-0">
                        <td className="px-4 py-1.5 text-sm text-ink">{m.name}</td>
                        <td className="px-4 py-1.5 text-xs num text-muted-dark">{monthLabel(m.demand_month)}</td>
                        <td className="px-4 py-1.5 text-right num text-sm text-ink">{((m.milestone_pct || 0) * 100).toFixed(1)}%</td>
                        <td className="px-4 py-1.5 text-right num text-xs text-muted-dark">{m.collection_lag_months ?? 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Trend footer */}
          <div className="flex items-center justify-between text-xs text-muted-dark px-1">
            <div className="flex items-center gap-2">
              <span>Trend</span>
              <Sparkline points={monthly} tone="gold" width={120} height={26} />
            </div>
            <span className="text-[11px] text-muted-light">
              {(project.milestone_months || []).length} milestone months in horizon
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, tone, testId }) {
  const valCls = tone === "gold" ? "gradient-text" : "text-ink";
  return (
    <div data-testid={testId} className="cf-card p-4 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1.5">
        {label}
      </div>
      <div
        className={`num ${valCls} truncate`}
        style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em", lineHeight: 1.1 }}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-muted-dark mt-1 truncate">{sub}</div>}
    </div>
  );
}
